import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Zap, Dumbbell, Target } from 'lucide-react';

const ProjetoX = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section className="relative overflow-hidden bg-[#0a0a0a]" id="projeto-x" data-testid="projeto-x-section">
      {/* Diagonal green accent lines */}
      <div className="absolute inset-0 opacity-[0.06]">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 80px,
            #11830f 80px,
            #11830f 81px
          )`
        }} />
      </div>

      {/* Glowing green orb */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.06]" style={{
        background: 'radial-gradient(circle, #11830f 0%, transparent 60%)'
      }} />

      <div className="relative z-10">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#11830f] to-transparent" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="order-2 lg:order-1"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-[#11830f]/20 border border-[#11830f]/40 rounded-full px-4 py-2 mb-8"
              >
                <Zap className="w-4 h-4 text-[#11830f]" fill="#11830f" />
                <span className="text-[#11830f] text-sm font-bold tracking-widest uppercase">Novo Projeto</span>
              </motion.div>

              {/* Title */}
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-2 leading-[0.9] tracking-tight">
                PROJETO
              </h2>
              <h2 className="text-6xl md:text-7xl lg:text-8xl font-black mb-8 leading-[0.9] tracking-tight" style={{
                background: 'linear-gradient(135deg, #11830f 0%, #1aad17 50%, #11830f 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                X
              </h2>

              {/* Subtitle */}
              <div className="w-16 h-1 bg-[#11830f] mb-6" />
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8 max-w-lg">
                A fusão entre <span className="text-white font-bold">nutrição estratégica</span> e <span className="text-[#11830f] font-bold">treinamento de alta performance</span>. 
                Um programa completo para quem busca resultados reais, com acompanhamento nutricional integrado ao Cross Training.
              </p>

              {/* Features */}
              <div className="space-y-4 mb-10">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#11830f]/20 flex items-center justify-center flex-shrink-0">
                    <Dumbbell className="w-5 h-5 text-[#11830f]" />
                  </div>
                  <span className="text-gray-300 font-medium">Plano nutricional voltado para performance</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#11830f]/20 flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#11830f]" />
                  </div>
                  <span className="text-gray-300 font-medium">Estratégias de pré e pós-treino personalizadas</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#11830f]/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-[#11830f]" />
                  </div>
                  <span className="text-gray-300 font-medium">Acompanhamento integrado com treino e dieta</span>
                </div>
              </div>

              {/* CTA */}
              <a
                href="https://wa.me/5535988366028?text=Ol%C3%A1%20Thiago%2C%20tudo%20bem%3F%20Vi%20sobre%20o%20Projeto%20X%20no%20seu%20site%20e%20fiquei%20muito%20interessado(a)!%20Gostaria%20de%20saber%20mais%20detalhes%20sobre%20como%20funciona%20a%20integra%C3%A7%C3%A3o%20entre%20nutri%C3%A7%C3%A3o%20e%20Cross%20Training%2C%20e%20como%20posso%20participar."
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-[#11830f] text-white px-8 py-4 rounded-none font-bold text-base uppercase tracking-wider hover:bg-[#1aad17] transition-all shadow-[0_0_30px_rgba(17,131,15,0.3)] hover:shadow-[0_0_50px_rgba(17,131,15,0.5)] inline-flex items-center gap-3"
                data-testid="projeto-x-cta"
              >
                Quero participar
                <Zap className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </a>
            </motion.div>

            {/* Right - Video */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="order-1 lg:order-2"
            >
              <div
                className="relative rounded-2xl overflow-hidden border-2 border-[#11830f]/30 shadow-[0_0_40px_rgba(17,131,15,0.15)] cursor-pointer group"
                onClick={toggleVideo}
                data-testid="projeto-x-video"
              >
                <video
                  ref={videoRef}
                  className="w-full aspect-[9/16] max-h-[600px] object-cover"
                  playsInline
                  loop
                  preload="metadata"
                  poster=""
                >
                  <source src="https://customer-assets.emergentagent.com/job_43ec9f46-838b-45d5-be62-a91e74699348/artifacts/qfm9h1we_WhatsApp%20Video%202026-02-09%20at%2015.25.57.mp4" type="video/mp4" />
                </video>

                {/* Play/Pause overlay */}
                <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                  <div className="w-20 h-20 rounded-full bg-[#11830f]/90 backdrop-blur-sm flex items-center justify-center shadow-[0_0_30px_rgba(17,131,15,0.5)]">
                    {isPlaying ? (
                      <Pause className="w-8 h-8 text-white" fill="white" />
                    ) : (
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    )}
                  </div>
                </div>

                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#11830f]" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#11830f]" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#11830f]" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#11830f]" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#11830f] to-transparent" />
      </div>
    </section>
  );
};

export default ProjetoX;
